import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';
import { DateInput } from '../../../../shared/components/DateInput.js';

interface KpiRow {
  readonly kpiId: string;
  readonly kpiCode: string;
  readonly kpiName: string;
  readonly kpiCategory: string;
  readonly frequency: string;
  readonly aggregation: string;
  readonly unit: string | null;
  readonly targetValue: string | null;
  readonly status: string;
}

const CATEGORIES = ['OPERATIONAL', 'FINANCIAL', 'MARKETING', 'BRAND', 'SYSTEM', 'QUALITY', 'RISK', 'COMPLIANCE'];
const STATUSES = ['DRAFT', 'ACTIVE', 'SUSPENDED', 'RETIRED'];
const FREQUENCIES = ['REALTIME', 'HOURLY', 'DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL', 'ON_DEMAND'];
const AGGREGATIONS = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'RATIO', 'LAST', 'CUSTOM'];
const DIRECTIONS = ['ABOVE_IS_BAD', 'BELOW_IS_BAD', 'RANGE'];
const PERIOD_TYPES = ['DAILY', 'WEEKLY', 'MONTHLY', 'QUARTERLY', 'ANNUAL', 'HOURLY', 'REALTIME'];

/**
 * M37 — KPI Management Screen (§28 / §7 KPI Engine). Central register of KPI
 * definitions (T492) across the 8 categories, carrying formula (BR-01),
 * measurement frequency, aggregation, target and warning / critical thresholds
 * with breach direction, and lifecycle status. Authors create new KPIs
 * (KPI_MANAGE) and — kept distinct by SoD — record a measured value for the
 * selected period (KPI_TRACK, T493). Filterable by category and status. Tenant
 * isolation is enforced server-side (LAW 4). A 403 surfaces a clear permission
 * message.
 */
export function KpiManagementScreen(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<KpiRow[]>([]);
  const [total, setTotal] = useState(0);
  const [kpiCategory, setKpiCategory] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // Create-KPI form
  const [kpiName, setKpiName] = useState('');
  const [newCategory, setNewCategory] = useState(CATEGORIES[0]);
  const [formula, setFormula] = useState('');
  const [frequency, setFrequency] = useState('DAILY');
  const [aggregation, setAggregation] = useState('SUM');
  const [unit, setUnit] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [warningThreshold, setWarningThreshold] = useState('');
  const [criticalThreshold, setCriticalThreshold] = useState('');
  const [thresholdDirection, setThresholdDirection] = useState('ABOVE_IS_BAD');
  const [newStatus, setNewStatus] = useState('DRAFT');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [created, setCreated] = useState<KpiRow | null>(null);

  // Track-value form
  const [trackKpi, setTrackKpi] = useState<KpiRow | null>(null);
  const [periodType, setPeriodType] = useState('DAILY');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [actualValue, setActualValue] = useState('');
  const [trackTarget, setTrackTarget] = useState('');
  const [trackBusy, setTrackBusy] = useState(false);
  const [trackError, setTrackError] = useState<string | null>(null);
  const [tracked, setTracked] = useState<{ readonly kpiStatus: string; readonly variance: string | null } | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (kpiCategory) params.set('kpiCategory', kpiCategory);
    if (status) params.set('status', status);
    if (q) params.set('q', q);
    const qs = params.toString();
    apiRequest<{ data: KpiRow[]; total: number }>(`/v1/bi/kpis${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, kpiCategory, status, q, reload]);

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setCreated(null);
    if (!kpiName.trim()) { setActionError(t('m37.kpi.nameRequired')); return; }
    if (!formula.trim()) { setActionError(t('m37.kpi.formulaRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: KpiRow }>(`/v1/bi/kpis`, {
        method: 'POST',
        body: {
          kpiName: kpiName.trim(),
          kpiCategory: newCategory,
          formula: formula.trim(),
          frequency,
          aggregation,
          unit: unit.trim() || undefined,
          targetValue: targetValue.trim() ? Number(targetValue) : undefined,
          warningThreshold: warningThreshold.trim() ? Number(warningThreshold) : undefined,
          criticalThreshold: criticalThreshold.trim() ? Number(criticalThreshold) : undefined,
          thresholdDirection,
          status: newStatus,
        },
        token,
      });
      setCreated(r.data);
      setKpiName('');
      setFormula('');
      setUnit('');
      setTargetValue('');
      setWarningThreshold('');
      setCriticalThreshold('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m37.kpi.notAuthCreate'));
      } else {
        setActionError((err as Error).message);
      }
    } finally {
      setBusy(false);
    }
  };

  const submitTrack = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setTrackError(null);
    setTracked(null);
    if (!trackKpi) return;
    if (!periodStart || !periodEnd) { setTrackError(t('m37.kpi.periodRequired')); return; }
    setTrackBusy(true);
    try {
      const r = await apiRequest<{ data: { kpiStatus: string; variance: string | null } }>(`/v1/bi/kpis/${trackKpi.kpiId}/values`, {
        method: 'POST',
        body: {
          periodType,
          periodStart: new Date(periodStart).toISOString(),
          periodEnd: new Date(periodEnd).toISOString(),
          actualValue: actualValue.trim() ? Number(actualValue) : undefined,
          targetValue: trackTarget.trim() ? Number(trackTarget) : undefined,
        },
        token,
      });
      setTracked(r.data);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setTrackError(t('m37.kpi.notAuthTrack'));
      } else {
        setTrackError((err as Error).message);
      }
    } finally {
      setTrackBusy(false);
    }
  };

  return (
    <main aria-label="kpi-management">
      <h1>{t('m37.kpi.title')}</h1>

      <section aria-label="create-kpi">
        <h2>{t('m37.kpi.createTitle')}</h2>
        <form aria-label="kpi-form" onSubmit={(e) => void submit(e)}>
          <label>{t('m37.common.name')}<input aria-label="field-name" value={kpiName} onChange={(e) => setKpiName(e.target.value)} /></label>
          <label>{t('m37.common.category')}
            <select aria-label="field-category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.formula')}<input aria-label="field-formula" value={formula} onChange={(e) => setFormula(e.target.value)} placeholder={t('m37.kpi.formulaPlaceholder')} /></label>
          <label>{t('m37.kpi.frequency')}
            <select aria-label="field-frequency" value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              {FREQUENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.aggregation')}
            <select aria-label="field-aggregation" value={aggregation} onChange={(e) => setAggregation(e.target.value)}>
              {AGGREGATIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.unit')}<input aria-label="field-unit" value={unit} onChange={(e) => setUnit(e.target.value)} placeholder={t('m37.kpi.unitPlaceholder')} /></label>
          <label>{t('m37.kpi.target')}<input aria-label="field-target" type="number" value={targetValue} onChange={(e) => setTargetValue(e.target.value)} /></label>
          <label>{t('m37.kpi.warningThreshold')}<input aria-label="field-warning" type="number" value={warningThreshold} onChange={(e) => setWarningThreshold(e.target.value)} /></label>
          <label>{t('m37.kpi.criticalThreshold')}<input aria-label="field-critical" type="number" value={criticalThreshold} onChange={(e) => setCriticalThreshold(e.target.value)} /></label>
          <label>{t('m37.kpi.thresholdDirection')}
            <select aria-label="field-direction" value={thresholdDirection} onChange={(e) => setThresholdDirection(e.target.value)}>
              {DIRECTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="field-status" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              {STATUSES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <Button variant="primary" type="submit" aria-label="submit-kpi" disabled={busy}>{busy ? t('m37.common.saving') : t('m37.kpi.createBtn')}</Button>
        </form>
        {actionError && <p role="alert" aria-label="create-error">{actionError}</p>}
        {created && <p role="status" aria-label="create-result">{t('m37.kpi.created', { code: created.kpiCode, name: created.kpiName })}</p>}
      </section>

      <section aria-label="kpi-list">
        <h2>{t('m37.kpi.listTitle')}</h2>
        <div className="filters">
          <label>{t('m37.common.category')}
            <select aria-label="filter-category" value={kpiCategory} onChange={(e) => setKpiCategory(e.target.value)}>
              {['', ...CATEGORIES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.status')}
            <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
              {['', ...STATUSES].map((s) => <option key={s || 'all'} value={s}>{s || t('m37.common.all')}</option>)}
            </select>
          </label>
          <label>{t('m37.common.search')}<input aria-label="filter-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('m37.common.searchPlaceholder')} /></label>
        </div>
        {error && <p role="alert">{error}</p>}
        {loading ? <p>{t('common.loading')}</p> : (
          <>
            <p aria-label="kpi-count">{t('m37.kpi.count', { count: total })}</p>
            <DataTable
              ariaLabel="kpi-table"
              rows={rows}
              rowKey={(r) => r.kpiId}
              columns={[
                { key: 'kpiCode', header: t('m37.common.code'), render: (r) => r.kpiCode },
                { key: 'kpiName', header: t('m37.common.name'), render: (r) => r.kpiName },
                { key: 'kpiCategory', header: t('m37.common.category'), render: (r) => r.kpiCategory },
                { key: 'frequency', header: t('m37.kpi.frequency'), render: (r) => r.frequency },
                { key: 'unit', header: t('m37.common.unit'), render: (r) => r.unit ?? '—' },
                { key: 'targetValue', header: t('m37.kpi.target'), render: (r) => r.targetValue ?? '—' },
                { key: 'status', header: t('m37.common.status'), render: (r) => r.status },
                { key: 'actions', header: t('m37.common.actions'), render: (r) => <Button variant="secondary" type="button" aria-label={`track-${r.kpiId}`} onClick={() => { setTrackKpi(r); setTracked(null); setTrackError(null); }}>{t('m37.kpi.trackValue')}</Button> },
              ]}
            />
          </>
        )}
      </section>

      {trackKpi && (
        <section aria-label="track-value">
          <h2>{t('m37.kpi.trackTitle', { name: trackKpi.kpiName })}</h2>
          <form aria-label="track-form" onSubmit={(e) => void submitTrack(e)}>
            <label>{t('m37.kpi.periodType')}
              <select aria-label="track-period-type" value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
                {PERIOD_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
            <label>{t('m37.kpi.periodStart')}<DateInput aria-label="track-period-start" type="date" value={periodStart} onValueChange={(nextValue) => setPeriodStart(nextValue)} /></label>
            <label>{t('m37.kpi.periodEnd')}<DateInput aria-label="track-period-end" type="date" value={periodEnd} onValueChange={(nextValue) => setPeriodEnd(nextValue)} /></label>
            <label>{t('m37.kpi.actualValue')}<input aria-label="track-actual" type="number" value={actualValue} onChange={(e) => setActualValue(e.target.value)} /></label>
            <label>{t('m37.kpi.targetValue')}<input aria-label="track-target" type="number" value={trackTarget} onChange={(e) => setTrackTarget(e.target.value)} /></label>
            <Button variant="primary" type="submit" aria-label="submit-track" disabled={trackBusy}>{trackBusy ? t('m37.common.recording') : t('m37.kpi.recordValue')}</Button>
            <Button variant="danger" type="button" aria-label="cancel-track" onClick={() => setTrackKpi(null)}>{t('m37.common.close')}</Button>
          </form>
          {trackError && <p role="alert" aria-label="track-error">{trackError}</p>}
          {tracked && <p role="status" aria-label="track-result">{t('m37.kpi.trackResult', { status: tracked.kpiStatus, variance: tracked.variance ?? '—' })}</p>}
        </section>
      )}
    </main>
  );
}
